// src/components/CriticalitySettingsTable.tsx

import React, {useState, useMemo} from 'react'
import DataTable, {TableColumn} from 'react-data-table-component'
import {FiEdit, FiInbox} from 'react-icons/fi'
import CriticalitySettingsModal from '../modals/CriticalitySettingsModal'


type CombinedRow = {
  id: number
  severity: string // numeric string '1'–'6'
  category: string // human label
  systolic?: string
  diastolic?: string
  systolicId?: number
  diastolicId?: number
  color: string // full-strength color
}

// 🎨 Severity to Color
const severityColors: Record<string, string> = {
  '1': '#81D4FA', // blue
  '2': '#8BC34A', // green
  '3': '#FFCA28', // yellow
  '4': '#FF9800', // orange
  '5': '#F44336', // red
  '6': '#B71C1C', // dark red
}

// ✨ Lighten-3 “100” shades for the backgrounds
const severityLightColors: Record<string, string> = {
  '1': '#E3F2FD', // blue-100
  '2': '#DCEDC8', // green-100
  '3': '#FFF9C438', // yellow-100
  '4': '#FFE0B2', // orange-100
  '5': '#FFCDD2', // red-100
  '6': '#FFCDD2', // red-100 (for crisis)
}

// 🏷️ Severity to Label
const severityLabels: Record<string, string> = {
  '1': 'Low',
  '2': 'Normal',
  '3': 'Elevated',
  '4': 'Hypertension Stage 1',
  '5': 'Hypertension Stage 2',
  '6': 'Hypertensive Crisis',
}

// Merge raw settings into CombinedRow[]
const prepareTableData = (settings: any[]): CombinedRow[] => {
  const grouped: Record<string, CombinedRow> = {}

  settings.forEach(({id, severity, min_value, max_value, TelemetryType}) => {
    if (!grouped[severity]) {
      grouped[severity] = {
        id,
        severity,
        category: severityLabels[severity] || `Severity ${severity}`,
        systolic: '-',
        diastolic: '-',
        color: severityColors[severity] || '#e0e0e0',
      }
    }

    const range = `${min_value}-${max_value}`
    if (TelemetryType.name === 'systolic_pressure') {
      grouped[severity].systolic = range
      grouped[severity].systolicId = id
    }
    if (TelemetryType.name === 'diastolic_pressure') {
      grouped[severity].diastolic = range
      grouped[severity].diastolicId = id
    }
  })

  return Object.values(grouped)
}

const CriticalitySettingsTable: React.FC<{
  allowEdit?: boolean
  criticalitySettings: any[]
  onSave: (rowData: any) => void
}> = ({allowEdit = true, criticalitySettings, onSave}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<CombinedRow | null>(null)

  // prepare rows
  const data = useMemo(() => prepareTableData(criticalitySettings), [criticalitySettings])

  // build columns dynamically
  const columns: TableColumn<CombinedRow>[] = useMemo(() => {
    const cols: TableColumn<CombinedRow>[] = [
      {
        name: 'Severity',
        cell: (row) => (
          <span
            className='inline-block px-3 py-1 rounded-3 text-sm font-medium'
            style={{
              border: `2px solid ${row.color}`,
              backgroundColor: severityLightColors[row.severity],
              color: row.color,
              fontWeight: 'bold',
            }}
          >
            {row.category}
          </span>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        grow: 2,
      },
      {
        name: 'SYS',
        selector: (row) => row.systolic || '-',
        center: true,
      },
      {
        name: 'DIA',
        selector: (row) => row.diastolic || '-',
        center: true,
      },
    ]

    if (allowEdit) {
      cols.push({
        name: 'Action',
        cell: (row) => (
          <button
            onClick={() => {
              setSelectedRow(row)
              setModalOpen(true)
            }}
            className='p-1 rounded hover:bg-gray-100'
          >
            <FiEdit size={18} />
          </button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '80px',
      })
    }

    return cols
  }, [allowEdit])

  return (
    <div className='p-6 bg-white rounded-lg shadow-md'>
      {data.length > 0 ? (
        <DataTable
          columns={columns}
          data={data}
          // pagination
          highlightOnHover
          pointerOnHover={!!allowEdit}
          noHeader
        />
      ) : (
        <div className='flex flex-col items-center justify-center py-12 px-4 text-center'>
          <div className='mb-6 p-6 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors'>
            <FiInbox
              size={64}
              className='text-blue-500/80 hover:text-blue-600/90 transition-colors'
            />
          </div>
          <h3 className='text-xl font-semibold text-gray-700 mb-2'>No Criticality Settings</h3>
          <p>Please, click on Create Settings button to create settings for the patient</p>
        </div>
      )}

      {selectedRow && (
        <CriticalitySettingsModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          rowData={selectedRow}
          onSave={(values) => {
            onSave({...selectedRow, ...values})
            setModalOpen(false)
          }}
        />
      )}
    </div>
  )
}

export default CriticalitySettingsTable
